import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ApiError, fetchBigFiveSet, fetchResultPage, resultPdfUrl, sendFeedback, sendValidation } from '../api/client';
import type { BigFiveResult, BigFiveSet, DiagnosisResult } from '../api/types';
import { AdSlot } from '../components/AdSlot';
import { CompatSection } from '../components/CompatSection';
import { ReportTheme } from '../components/ReportTheme';
import { Portrait } from '../components/Portrait';
import { ShareButtons } from '../components/ShareButtons';
import { AXIS_LABELS, BIG_FIVE_DOMAIN_NAMES, BIG_FIVE_DOMAIN_ORDER, FACET_LABELS, MODE_INFO, SCALE_HINT, bigFiveLevel, jungianType } from '../diagnosis/labels';
import { MINI_IPIP_ITEMS, VALIDATION_CONSENT } from '../diagnosis/validation';

/**
 * 結果ページは結果 ID ごとに作り直す（`key`）。同じルートのまま /r/A → /r/B と移ると
 * （比べた結果の「相手の立場から読む」・相手のキャラの名前）、React は同じ部品を使い回すので、
 * B の URL のまま A の結果（と送った印・入力中の URL など）を出し続けていた。
 */
export function ResultPage() {
  const { resultId = '' } = useParams();
  return <ResultView key={resultId} resultId={resultId} />;
}

function ResultView({ resultId }: { resultId: string }) {
  const location = useLocation();
  const preloaded = (location.state as { result?: DiagnosisResult } | null)?.result;

  const [result, setResult] = useState<DiagnosisResult | null>(preloaded ?? null);
  /** その診断に広告を出してよいか（doc/ADS.md §5）。分かるまでは出さない。 */
  const [adsAllowed, setAdsAllowed] = useState(false);
  const [error, setError] = useState('');

  /*
   * 結果ページは `GET /r/:resultId` を呼ぶ。結果本体（view）と、広告を出してよいか（adsAllowed）が一度に返る。
   * 診断直後は結果を state で持って来ているので、その場合は adsAllowed だけ使う。
   */
  useEffect(() => {
    if (!resultId) return;
    let cancelled = false;
    void fetchResultPage(resultId)
      .then((value) => {
        if (cancelled) return;
        setResult((current) => current ?? value.view);
        setAdsAllowed(value.adsAllowed);
      })
      .catch((cause: unknown) => {
        if (cancelled || result) return; // 結果を持っていれば、取れなくても表示は止めない
        setError(cause instanceof ApiError ? cause.message : '結果を読み込めませんでした。');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultId]);

  if (error) {
    return (
      <main className="page">
        <h1 className="page__title">結果が見つかりませんでした</h1>
        <p className="page__lead">{error}</p>
        <Link className="button button--primary" to="/">トップへ戻る</Link>
      </main>
    );
  }
  if (!result) return <p className="page__notice">結果を開いています…</p>;

  const { character, match, theme } = result;

  return (
    <main className="page result">
      <p className="result__kicker">
        <Link className="result__theme" to={theme.href}>{theme.title}</Link>　あなたに、いちばん近いのは
      </p>

      <section className="hero">
        <Portrait brief={character} size={220} className="hero__portrait" />
        <div className="hero__body">
          <p className="hero__label">{character.label}</p>
          <p className="hero__epithet">{character.epithet}</p>
          <h1 className="hero__name">{character.name}</h1>
          <p className="hero__similarity">
            一致度 <strong>{match.similarity}</strong>％
          </p>
        </div>
      </section>

      {match.close && match.runnerUp && (
        <section className="panel panel--close">
          <h2 className="panel__title">あなたは、この二つの間にいます</h2>
          <p>
            {character.name}（{match.similarity}％）と {match.runnerUp.name}（{match.runnerUp.similarity}％）の差はわずか {match.margin} ポイントでした。どちらの性質も、あなたの中にあります。
          </p>
          <div className="mini">
            <Portrait brief={match.runnerUp} size={120} className="mini__portrait" loading="lazy" />
            <div>
              <p className="mini__epithet">{match.runnerUp.epithet}</p>
              <p className="mini__name">{match.runnerUp.name}</p>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <h2 className="section__title">なぜ、あなたは{character.name}なのか</h2>
        <ol className="reasons">
          {result.reasons.map((reason) => (
            <li key={reason.facet} className="reason">
              <p className="reason__facet">{reason.facetLabel}</p>
              <h3 className="reason__headline">{reason.headline}</h3>
              <p className="reason__text">{reason.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section">
        <h2 className="section__title">{character.name}について</h2>
        <p className="section__body">{character.summary}</p>
      </section>

      {result.partner && (
        <section className="section">
          <h2 className="section__title">あなたの相棒</h2>
          <div className="mini mini--large">
            <Portrait brief={result.partner} size={120} className="mini__portrait" loading="lazy" />
            <div>
              <p className="mini__label">{result.partner.label}</p>
              <p className="mini__epithet">{result.partner.epithet}</p>
              <p className="mini__name">{result.partner.name}</p>
              <p className="mini__reason">{result.partner.reason}</p>
            </div>
          </div>
          {result.rival && (
            <p className="section__note">
              対になる相手 — <strong>{result.rival.name}</strong>（{result.rival.epithet}）
            </p>
          )}
        </section>
      )}

      {/* 広告は結果（キャラ・理由・相棒）を読み終えたところに置く。広告を切ったサイトでは何も描かない */}
      <AdSlot placement="result" allowed={adsAllowed} />

      {/* ビッグファイブ版は 5 因子が主結果。型とキャラは 5 因子から写した目安として同じ節に添える（doc/ASSESSMENT.md §9） */}
      {result.bigFive ? <BigFiveSection result={result} bigFive={result.bigFive} /> : <TypeSection result={result} />}

      {/* 相性と付き合い方。相手の結果 URL を貼る／型どうしの一般論を選ぶ（doc/ASSESSMENT.md §10） */}
      <CompatSection result={result} shareUrl={shareUrlOf(result)} />

      {result.retest && <RetestSection result={result} />}

      <FeedbackSection resultId={result.resultId} />

      <ValidationSection resultId={result.resultId} validated={result.validated} />

      <section className="section">
        <h2 className="section__title">結果を PDF で保存する</h2>
        <p className="section__body">
          キャラクターの絵と、見立てた理由・性格の軸・測り方の説明を 1 つの PDF にまとめます。就職や転職の自己分析、面接の話題づくりにどうぞ。
          結果ページの URL も載るので、相手はそこから同じ結果を確かめられます（氏名などは載りません）。
        </p>
        <div className="page__actions">
          <a className="button button--primary" href={resultPdfUrl(result.resultId)} download>PDF をダウンロード</a>
        </div>
        <p className="section__note">性格の傾向を自己申告から見立てたもので、適性や能力を判定するものではありません。</p>
        <p className="section__note">
          「相性と付き合い方」はこの PDF には載りません。相手の結果と比べた詳細版は、<a href="#compat">その節</a>から別の PDF にできます（型どうしの一般論は PDF になりません）。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">結果をシェアする</h2>
        <p className="section__body">
          リンクを開いた人は、この結果と <Link to={theme.href}>「{theme.title}」の入口</Link> に飛べます。
        </p>
        <ShareButtons url={shareUrlOf(result)} text={shareTextOf(result)} title={`GenDan ${theme.title}`} />
        <div className="page__actions result__again">
          <Link className="button button--ghost" to={theme.href}>もう一度診断する</Link>
          <Link className="button button--ghost" to={retestHref(result)}>同じ深さでもう一度（安定性の確認）</Link>
          <Link className="button button--ghost" to="/">自分でも診断を作る</Link>
        </div>
        <p className="section__note">
          2〜4 週間あけて「同じ深さでもう一度」を受けると、前回との比較が出ます。結果がどれだけ安定するか（再検査信頼性）を確かめる材料になります。
        </p>
        {result.mode !== 'deep' && result.mode !== 'bigfive' && (
          <p className="section__note">
            {result.mode === 'quick' ? '短い版' : '主軸だけ'}の診断です。<Link to={theme.href}>じっくり（72 問）で受け直す</Link>と、18 の側面すべてを測り、見立ての精度が上がります。
          </p>
        )}
        {result.mode === 'bigfive' && (
          <p className="section__note">
            ビッグファイブ（性格 5 因子・60 問）での診断です。キャラと型は 5 因子から 6 本の軸に写した目安なので、
            <Link to={theme.href}>じっくり（72 問）で受ける</Link>と、軸そのものを測った見立てになります。
          </p>
        )}
        {result.confidence < 50 && (
          <p className="section__note">
            回答のばらつきが大きめでした。もう一度落ち着いて受けると、より近いキャラが見つかるかもしれません。
          </p>
        )}
        {/* 相性ドラマの動画のリクエスト（診断の入口の下にある。doc/VIDEO.md §8） */}
        <p className="section__note">
          <Link to={`/t/${encodeURIComponent(result.theme.themeId)}?video=${encodeURIComponent(result.character.id)}#video-request`}>
            {result.character.name}と誰かの相性ドラマの動画をリクエストする
          </Link>
          （費用がかかるため、作れるのはごく一部です）
        </p>
        {/* 結果を消したいときの行き先。URL を添える手間を省くため、結果 ID を付けて開く（doc/AUTH.md §14） */}
        <p className="section__note">
          この結果を消したいときは、ログインして受けた結果なら<Link to="/me">自分のページ</Link>の「受けた診断」から消せます。
          ログインせずに受けた結果は<Link to={`/contact?about=result&result=${result.resultId}`}>お問い合わせ</Link>からご依頼ください。
        </p>
      </section>

      <p className="disclaimer">{result.disclaimer}</p>
      <ReportTheme themeId={result.theme.themeId} resultId={result.resultId} />
    </main>
  );
}

/** タイプコードと軸スコア。「どう測ったか」の開示として、キャラの下に副次的に出す。 */
function TypeSection({ result }: { result: DiagnosisResult }) {
  const measuredAxes = AXIS_LABELS.filter((axis) => result.axisScores[axis.axis] !== undefined);
  const modeLabel = MODE_INFO.find((m) => m.mode === result.mode)?.label ?? '';
  // ユングの類型は主軸 4 文字から読む。8 人・4 人のテーマ（INT・IN）では 4 文字そろわないので出さない
  const jung = jungianType(result.displayType ?? result.type);
  // 「さっと」は 1 つの側面を 3 問（8 側面 × 3 = 24 問）、ほかは 4 問（questions.json の modes）
  const perFacet = result.mode === 'quick' ? 3 : 4;

  return (
    <section className="section">
      <h2 className="section__title">どう測ったか</h2>
      <p className="section__body">
        {jung ? (
          <>
            {modeLabel}での回答から、ユングの類型でいえば主機能は <strong>{jung.dominant}型</strong>、補助機能は{jung.auxiliary}
            （<strong className="typecode">{result.displayType ?? result.type}</strong>）でした（<Link to="/help/jung">読み方</Link>）。
          </>
        ) : (
          <>{modeLabel}での回答から出た型は <strong className="typecode">{result.displayType ?? result.type}</strong> でした。</>
        )}
        {result.characterType && result.characterType !== (result.displayType ?? result.type)
          ? `いちばん近いキャラの型は ${result.characterType} で、文字は一部違います。`
          : ''}
        キャラは型の文字ではなく、下の連続したスコアの「向き」がいちばん似ている相手を選んでいます。型の文字は軸ごとに 50 を境に決まるので、境に近い軸では食い違うことがあります。
      </p>

      <ul className="axes">
        {measuredAxes.map((axis) => {
          const value = result.axisScores[axis.axis];
          const contested = result.contestedAxes.includes(axis.axis);
          const certainty = result.axisCertainty?.[axis.axis];
          const side = value >= 50 ? axis.high : axis.low;
          return (
            <li key={axis.axis} className="axis">
              <div className="axis__head">
                <span className="axis__pole">{axis.low.code} {axis.low.name}</span>
                <span className="axis__caption">{axis.caption}</span>
                <span className="axis__pole">{axis.high.name} {axis.high.code}</span>
              </div>
              <div className="axis__bar">
                {/* 端に振り切った軸で丸が切れないよう、位置だけ内側に寄せる。 */}
                <span className="axis__marker" style={{ left: `${Math.min(97, Math.max(3, value))}%` }} />
              </div>
              {contested ? (
                <p className="axis__contested">{axis.low.code} と {axis.high.code} の境にいます</p>
              ) : certainty !== undefined ? (
                <p className="axis__certainty">{side.name}（{side.code}）である確からしさ {Math.round(certainty * 100)}%</p>
              ) : null}
            </li>
          );
        })}
      </ul>

      <details className="method">
        <summary>この数字の読み方</summary>
        <ul className="method__list">
          <li>1 つの側面（facet）を {perFacet} 問、1 つの軸を {perFacet * (result.mode === 'quick' ? 2 : 3)} 問で測っています。全 {result.measuredFacets.length * perFacet} 問。</li>
          <li>
            「確からしさ」は、軸のスコアが中央（50）から測定の誤差幅の何倍離れているかを確率にしたものです。
            {result.normVersion > 0
              ? ' 誤差幅は実際に受けた人のデータから出しています。'
              : ' いまは基準集団が集まるまでの暫定の誤差幅（信頼性 0.75 相当）で計算しています。'}
          </li>
          <li>
            {result.normVersion > 0
              ? 'スコアは、これまでに受けた人の中での位置（百分位）です。'
              : 'スコアはいまは素点（回答の平均）です。受けた人が 300 人を超えたら、その中での位置（百分位）に切り替わります。'}
          </li>
          <li>キャラは、各側面の 50 からのずれの「向き」がいちばん似ている相手（コサイン類似度）で決めています。濃さではなく組み合わせで決めるので、特定の相手ばかりが出ることを防いでいます。</li>
          <li>結果は自己申告にもとづく傾向の見立てで、資格を持つ専門家による心理検査の代わりにはなりません。</li>
        </ul>
      </details>

      <details className="facets">
        <summary>{result.measuredFacets.length}の側面ごとのスコアを見る</summary>
        <ul className="facets__list">
          {result.measuredFacets.map((facet) => (
            <li key={facet} className="facets__item">
              <span className="facets__name">{FACET_LABELS[facet] ?? facet}</span>
              <span className="facets__bar">
                <span className="facets__fill" style={{ width: `${result.facetScores[facet]}%` }} />
              </span>
              <span className="facets__value">{result.facetScores[facet]}</span>
            </li>
          ))}
        </ul>
      </details>

      <p className="section__note">信頼度 {result.confidence} ／ 100</p>
    </section>
  );
}

const pct = (n: number) => Math.min(100, Math.max(0, n));

/**
 * ビッグファイブ版の「どう測ったか」（doc/ASSESSMENT.md §9）。
 * 主結果は 5 因子と 15 側面の連続スコア。型ではないので「確からしさ」ではなく、スコア ± 測定の標準誤差の帯で誤差を見せる。
 * 因子・側面の名前と説明文は bigfive.json（`fetchBigFiveSet`）から読む（二重に持たない）。届く前は因子名だけで描く。
 * 型とキャラは 5 因子から 6 軸に写した目安なので、その旨と写し方を同じ節で明示する。
 */
function BigFiveSection({ result, bigFive }: { result: DiagnosisResult; bigFive: BigFiveResult }) {
  const [set, setSet] = useState<BigFiveSet | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchBigFiveSet().then((value) => { if (!cancelled) setSet(value); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const normed = bigFive.normVersion > 0;
  const displayType = result.displayType ?? result.type;
  const jung = jungianType(displayType);
  const domains = set?.domains ?? BIG_FIVE_DOMAIN_ORDER.map((id) => ({ id, name: BIG_FIVE_DOMAIN_NAMES[id], en: '', measures: '', high: '', low: '', facets: [] as readonly string[] }));
  const facetMeta = (id: string) => set?.facets.find((f) => f.id === id) ?? null;

  return (
    <section className="section">
      <h2 className="section__title">どう測ったか</h2>
      <p className="section__body">
        ビッグファイブ（性格 5 因子）の 60 問での回答です。5 つの因子と 15 の側面を 0〜100 で出します。
        {normed ? 'スコアは、これまでに受けた人の中での位置（百分位）です。' : 'スコアはいまは素点で、50 が「どちらともいえない」にあたります。'}
        丸の周りの帯は測定の誤差（±1 標準誤差）で、本当の値がその幅に入る見込みはおよそ 68% です。帯が重なる因子どうしの大小は、確かとは言えません。読み方は<Link to="/help/bigfive">ビッグファイブについて</Link>。
      </p>

      <ul className="axes">
        {domains.map((d) => {
          const score = bigFive.domains[d.id];
          const sem = bigFive.domainSem[d.id];
          const level = bigFiveLevel(score, normed);
          const text = level === '中程度' ? 'どちらの傾向も、場面によって出ます。' : level === '高い' || level === 'やや高い' ? d.high : d.low;
          const lo = pct(score - sem);
          const hi = pct(score + sem);
          return (
            <li key={d.id} className="axis">
              <div className="axis__head">
                <span className="axis__pole">低い</span>
                <span className="axis__caption axis__caption--wide"><strong>{d.name}</strong>{d.en ? `（${d.en}）` : ''}{d.measures ? ` — ${d.measures}` : ''}</span>
                <span className="axis__pole">高い</span>
              </div>
              <div className="axis__bar">
                <span className="axis__band" style={{ left: `${lo}%`, width: `${hi - lo}%` }} />
                <span className="axis__marker" style={{ left: `${Math.min(97, Math.max(3, score))}%` }} />
              </div>
              <p className="axis__certainty">
                <span className="bigfive__level">{score}（{level}）</span>　{text}　誤差の幅 ±{sem}
              </p>
            </li>
          );
        })}
      </ul>

      <details className="facets">
        <summary>15 の側面ごとのスコアを見る</summary>
        {domains.map((d) => (
          <div key={d.id}>
            <p className="bigfive__group">{d.name}</p>
            <ul className="facets__list">
              {(d.facets.length > 0 ? d.facets : Object.keys(bigFive.facets).filter((f) => f.startsWith(`${d.id}.`))).map((fid) => {
                const meta = facetMeta(fid);
                const value = bigFive.facets[fid];
                return (
                  <li key={fid} className="facets__item">
                    <span className="facets__name">{meta?.name ?? fid}</span>
                    <span className="facets__bar"><span className="facets__fill" style={{ width: `${pct(value)}%` }} /></span>
                    <span className="facets__value">{value}</span>
                    {meta && <span className="facets__hint">{value >= 50 ? meta.high : meta.low}（誤差 ±{bigFive.facetSem[fid]}）</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </details>

      <details className="method">
        <summary>この数字の読み方</summary>
        <ul className="method__list">
          <li>1 つの側面を 4 問（正 2・逆転 2）、1 つの因子を 3 側面 12 問で測っています。全 60 問。因子と側面の分け方は、現在もっとも使われている質問紙のひとつ BFI-2 と同じです（項目文は GenDan が書いたもの）。</li>
          <li>
            誤差の幅は、測定の標準誤差 SEM（標準偏差 × √(1 − 信頼性)）です。
            {normed ? ' 実際に受けた人のデータから出しています。' : ' いまは基準集団が集まるまでの暫定値（因子の信頼性 0.85、側面 0.70 相当）で計算しています。'}
          </li>
          <li>
            {normed
              ? 'スコアは、これまでに受けた人の中での位置（百分位）です。'
              : 'スコアはいまは素点（回答の平均）で、「高い・低い」は目盛りの中央 50 から見た言い方です。受けた人が 300 人を超えたら、その中での位置（百分位）に切り替わります。'}
          </li>
          <li>「神経症傾向」は研究で使われている因子名で、病気を指すものではありません。高いほど不快な感情を感じやすく揺れやすいこと、低いほど落ち着いていることを表します。</li>
          <li>結果は自己申告にもとづく傾向の見立てで、資格を持つ専門家による心理検査の代わりにはなりません。</li>
        </ul>
      </details>

      <div className="panel">
        <h3 className="panel__title">キャラと型への当てはめ（目安）</h3>
        <p>
          5 因子を 6 本の軸に写して型 <strong className="typecode">{displayType}</strong> を出し、その向きがいちばん似ているキャラを選んでいます。
          {jung ? `ユングの類型でいえば主機能は${jung.dominant}型、補助機能は${jung.auxiliary}にあたります（` : '（'}<Link to="/help/jung">読み方</Link>）。写し方は、実証研究で報告された対応（外向性 → 外向／内向、開放性 → 直観／感覚、協調性 → 感情／思考、誠実性 → 判断／知覚、外向性の自己主張 → 主張型／受容型、神経症傾向 → 繊細型／泰然型）に沿っていますが、<strong>無理やりの当てはめ</strong>です。
          {result.characterType && result.characterType !== displayType ? `いちばん近いキャラの型は ${result.characterType} で、文字は一部違います。` : ''}
          詳しくは<Link to="/help/bigfive#mapping">写し方の説明</Link>。
        </p>
        <ul className="facets__list">
          {AXIS_LABELS.map((axis) => {
            const value = result.axisScores[axis.axis];
            if (value === undefined) return null;
            const side = value >= 50 ? axis.high : axis.low;
            const certainty = result.axisCertainty?.[axis.axis];
            const contested = result.contestedAxes.includes(axis.axis);
            return (
              <li key={axis.axis} className="facets__item">
                <span className="facets__name">{axis.caption}</span>
                <span className="facets__bar"><span className="facets__fill" style={{ width: `${pct(value)}%` }} /></span>
                <span className="facets__value">{value}</span>
                <span className="facets__hint">
                  {contested ? `${axis.low.code} と ${axis.high.code} の境にいます` : `${side.name}（${side.code}）${certainty !== undefined ? `・確からしさ ${Math.round(certainty * 100)}%` : ''}`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <p className="section__note">信頼度 {result.confidence} ／ 100</p>
    </section>
  );
}

/** 再受検のリンク。同じテーマ・同じ深さで、前回の結果 ID を持って診断へ。 */
function retestHref(result: DiagnosisResult): string {
  const theme = `theme=${encodeURIComponent(result.theme.themeId)}&`;
  return `/shindan?${theme}mode=${result.mode}&retest=${encodeURIComponent(result.resultId)}`;
}

/** 前回との比較（再受検）。軸ごとの差と、型・キャラが変わったかを出す。 */
function RetestSection({ result }: { result: DiagnosisResult }) {
  const previous = result.retest;
  if (!previous) return null;
  const axes = AXIS_LABELS.filter((a) => result.axisScores[a.axis] !== undefined && previous.axisScores[a.axis] !== undefined);
  const sameType = previous.type === result.type;
  const sameCharacter = previous.characterId === result.character.id;
  const date = previous.createdAt.slice(0, 10);
  // 両方ビッグファイブ版なら、写した軸ではなく 5 因子そのものを比べる
  const domains = result.bigFive && previous.bigFiveDomains
    ? BIG_FIVE_DOMAIN_ORDER.map((d) => ({ key: d, label: BIG_FIVE_DOMAIN_NAMES[d], before: previous.bigFiveDomains?.[d] as number, after: result.bigFive?.domains[d] as number }))
    : null;
  const rows = domains ?? axes.map((a) => ({ key: a.axis, label: a.caption, before: previous.axisScores[a.axis], after: result.axisScores[a.axis] }));
  return (
    <section className="section">
      <h2 className="section__title">前回（{date}、{previous.daysBetween} 日前）との比較</h2>
      <p className="section__body">
        {domains ? '5 因子から写した型は' : '測定した型コードは'} {previous.type} → {result.type}（{sameType ? '同じ' : '変わった'}）。
        {previous.characterName ? `いちばん近い相手は ${previous.characterName} → ${result.character.name}（${sameCharacter ? '同じ' : '変わった'}）。` : ''}
      </p>
      <ul className="retest">
        {rows.map((row) => {
          const diff = row.after - row.before;
          return (
            <li key={row.key} className="retest__row">
              <span className="retest__axis">{row.label}</span>
              <span className="retest__values">{row.before} → {row.after}</span>
              <span className={`retest__diff${Math.abs(diff) >= 15 ? ' retest__diff--big' : ''}`}>{diff > 0 ? `+${diff}` : diff}</span>
            </li>
          );
        })}
      </ul>
      <p className="section__note">
        差が 15 を超える軸は、前回か今回のどちらかで状況や気分の影響が出ている可能性があります。2〜4 週間あけた再受検は、診断の安定性（再検査信頼性）の算出に使わせてもらいます。
      </p>
    </section>
  );
}

/** 併存尺度（研究協力・任意）。確立した性格 5 因子の短い尺度に答えてもらい、GenDan の軸との対応を検証する。 */
function ValidationSection({ resultId, validated }: { resultId: string; validated: boolean }) {
  const [open, setOpen] = useState(false);
  const [consented, setConsented] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'failed'>(validated ? 'done' : 'idle');
  const remaining = MINI_IPIP_ITEMS.filter((item) => answers[item.id] === undefined).length;

  const send = async () => {
    setState('sending');
    try {
      await sendValidation(resultId, answers);
      setState('done');
    } catch {
      setState('failed');
    }
  };

  if (state === 'done') {
    return (
      <section className="section validation">
        <h2 className="section__title">研究へのご協力</h2>
        <p className="section__note">ありがとうございます。確立した尺度との対応を確かめるのに使わせてもらいます。</p>
      </section>
    );
  }

  return (
    <section className="section validation">
      <h2 className="section__title">研究にご協力ください（任意・20 問・約 2 分）</h2>
      <p className="section__body">{VALIDATION_CONSENT}</p>
      {!open ? (
        <div className="page__actions">
          <button className="button button--ghost" type="button" onClick={() => setOpen(true)}>20 問に答える</button>
        </div>
      ) : (
        <>
          <label className="validation__consent">
            <input type="checkbox" checked={consented} onChange={(event) => setConsented(event.target.checked)} />
            上の説明を読み、研究への利用に同意します
          </label>
          <ol className="validation__list">
            {MINI_IPIP_ITEMS.map((item) => (
              <li key={item.id} className="validation__item">
                <p className="validation__text">{item.text}</p>
                <div className="likert likert--compact" role="radiogroup" aria-label={item.text}>
                  {SCALE_HINT.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      className={`likert__button${answers[item.id] === i + 1 ? ' likert__button--selected' : ''}`}
                      aria-pressed={answers[item.id] === i + 1}
                      title={label}
                      onClick={() => setAnswers((prev) => ({ ...prev, [item.id]: i + 1 }))}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ol>
          <p className="section__note">1 = まったく違う、5 = まさにそう。残り {remaining} 問。</p>
          <div className="page__actions">
            <button className="button button--primary" type="button" disabled={!consented || remaining > 0 || state === 'sending'} onClick={() => void send()}>
              {state === 'sending' ? '送っています…' : '送る'}
            </button>
          </div>
          {state === 'failed' && <p className="maker__error" role="alert">送れませんでした。時間をおいてもう一度お試しください。</p>}
        </>
      )}
    </section>
  );
}

/** 「しっくりきた？」。最も安価で強い精度の指標になる（doc/assessment.md §5-5）。 */
function FeedbackSection({ resultId }: { resultId: string }) {
  const [sent, setSent] = useState<'yes' | 'no' | null>(null);

  const answer = (fit: 'yes' | 'no') => {
    setSent(fit);
    void sendFeedback(resultId, fit).catch(() => undefined);
  };

  return (
    <section className="section feedback">
      <h2 className="section__title">この結果、しっくりきましたか？</h2>
      {sent ? (
        <p className="section__note">ありがとうございます。診断の改善に使わせてもらいます。</p>
      ) : (
        <div className="page__actions">
          <button className="button button--ghost" type="button" onClick={() => answer('yes')}>はい</button>
          <button className="button button--ghost" type="button" onClick={() => answer('no')}>いいえ</button>
        </div>
      )}
    </section>
  );
}

function shareUrlOf(result: DiagnosisResult): string {
  return result.shareUrl.startsWith('http') ? result.shareUrl : `${window.location.origin}/r/${result.resultId}`;
}

/** シェア文。診断名を入れて、見た人がその診断の入口に来られるようにする。 */
function shareTextOf(result: DiagnosisResult): string {
  const { theme, character } = result;
  const tag = `#GenDan #${theme.title.replace(/\s+/g, '')}`;
  return `「${theme.title}」でわたしは${character.emoji ?? ''}${character.name}（${character.epithet}）でした。あなたはどの${theme.subject}？ ${tag}`;
}
